import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/dateFmt';
import {
  AlertCircle,
  FileText,
  Save,
  RotateCcw,
  Eye,
  Code,
} from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';
import { SkeletonList } from '../../components/ui/Skeleton';
import { api, ApiError } from '../../lib/api';

interface Template {
  key: string;
  subject: string;
  html: string;
  text: string;
  sample_vars: Record<string, unknown>;
  updated_at: string;
  updated_by_user_id: string | null;
}

interface Rendered {
  subject: string;
  html: string;
  text: string;
}

const ALLOWED = ['admin', 'super_admin'];

const PLACEHOLDER_HINTS: Record<string, string> = {
  certificate: '{{first_name}}, {{full_name}}, {{filename}}',
  feedback_survey: '{{first_name}}, {{url}}',
};

/**
 * Admin editor for transactional email templates. List on the left,
 * three-pane editor (subject / html / text) on the right with a live
 * preview pane below that renders against sample_vars.
 */
export function EmailTemplates() {
  const { data: user, isLoading } = useCurrentUser();
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !user.roles.some((r) => ALLOWED.includes(r))) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api<{ rows: Template[] }>('/api/admin/email-templates');
        if (!cancelled) {
          setTemplates(r.rows);
          if (!selectedKey && r.rows.length > 0) setSelectedKey(r.rows[0]!.key);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'load failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const selected = useMemo(
    () => templates?.find((t) => t.key === selectedKey) ?? null,
    [templates, selectedKey],
  );

  if (isLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  if (!user.roles.some((r) => ALLOWED.includes(r))) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Email templates is restricted to admin / super_admin.
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
            <FileText className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-accent">
              Admin
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Email templates
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              Edit the wording the system sends out. Placeholders like{' '}
              <code className="rounded bg-surface-2 px-1 py-0.5 font-mono text-[11px]">
                {'{{first_name}}'}
              </code>{' '}
              are substituted at send time; the HTML field auto-escapes
              substituted values, the plain-text and subject fields don't.
            </p>
          </div>
        </header>

        {error ? (
          <div className="mt-4 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        <div className="mt-6 grid gap-4 lg:grid-cols-[220px_1fr]">
          <aside className="overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
            {templates === null ? (
              <div className="p-4">
                <SkeletonList rows={4} />
              </div>
            ) : (
              <ul className="divide-y divide-divider/10">
                {templates.map((t) => (
                  <li key={t.key}>
                    <button
                      type="button"
                      onClick={() => setSelectedKey(t.key)}
                      className={`flex w-full flex-col items-start gap-0.5 px-3 py-2.5 text-left transition-colors ${
                        selectedKey === t.key
                          ? 'bg-accent/10 text-accent'
                          : 'hover:bg-surface-2'
                      }`}
                    >
                      <span className="text-sm font-medium">{t.key}</span>
                      <span className="text-[11px] text-fg-muted line-clamp-1">
                        {t.subject}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <section>
            {selected ? (
              <TemplateEditor
                template={selected}
                onSaved={(updated) =>
                  setTemplates((prev) =>
                    prev?.map((x) => (x.key === updated.key ? updated : x)) ?? prev,
                  )
                }
              />
            ) : (
              <div className="rounded-lg border border-dashed border-line-strong/20 bg-surface p-8 text-center text-sm text-fg-muted">
                Pick a template on the left.
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}

function TemplateEditor({
  template,
  onSaved,
}: {
  template: Template;
  onSaved: (t: Template) => void;
}) {
  const [subject, setSubject] = useState(template.subject);
  const [html, setHtml] = useState(template.html);
  const [text, setText] = useState(template.text);
  const [sampleVars, setSampleVars] = useState(
    JSON.stringify(template.sample_vars, null, 2),
  );
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [rendered, setRendered] = useState<Rendered | null>(null);
  const [previewMode, setPreviewMode] = useState<'rendered' | 'source'>('rendered');
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSubject(template.subject);
    setHtml(template.html);
    setText(template.text);
    setSampleVars(JSON.stringify(template.sample_vars, null, 2));
    setRendered(null);
    setPreviewError(null);
  }, [template]);

  const dirty =
    subject !== template.subject ||
    html !== template.html ||
    text !== template.text ||
    sampleVars !== JSON.stringify(template.sample_vars, null, 2);

  async function preview() {
    setPreviewing(true);
    setPreviewError(null);
    try {
      let vars: Record<string, unknown> = {};
      try {
        vars = JSON.parse(sampleVars || '{}');
      } catch {
        setPreviewError('Sample vars must be valid JSON');
        setPreviewing(false);
        return;
      }
      const r = await api<{ rendered: Rendered }>(
        `/api/admin/email-templates/${template.key}/preview`,
        {
          method: 'POST',
          body: JSON.stringify({ subject, html, text, vars }),
        },
      );
      setRendered(r.rendered);
      // Pull the preview panel into view; the editor above is tall
      // enough that the rendered output would otherwise be off-screen.
      requestAnimationFrame(() => {
        previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (err) {
      setPreviewError(err instanceof ApiError ? err.message : 'preview failed');
    } finally {
      setPreviewing(false);
    }
  }

  async function save() {
    let parsed: Record<string, unknown> = {};
    try {
      parsed = JSON.parse(sampleVars || '{}');
    } catch {
      toast.error('Sample vars must be valid JSON');
      return;
    }
    setSaving(true);
    try {
      const r = await api<{ template: Template }>(
        `/api/admin/email-templates/${template.key}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            subject,
            html,
            text,
            sample_vars: parsed,
          }),
        },
      );
      onSaved(r.template);
      toast.success(`Saved ${template.key}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'save failed');
    } finally {
      setSaving(false);
    }
  }

  function revert() {
    setSubject(template.subject);
    setHtml(template.html);
    setText(template.text);
    setSampleVars(JSON.stringify(template.sample_vars, null, 2));
    setRendered(null);
    setPreviewError(null);
  }

  const hint = PLACEHOLDER_HINTS[template.key];

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-line/10 bg-surface p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
              {template.key}
            </div>
            {hint ? (
              <div className="mt-0.5 text-[11px] text-fg-muted">
                Placeholders: <code className="font-mono">{hint}</code>
              </div>
            ) : null}
            <div className="mt-0.5 text-[11px] text-fg-dim">
              Last saved {formatDateTime(template.updated_at)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<RotateCcw className="h-3.5 w-3.5" />}
              onClick={revert}
              disabled={!dirty || saving}
            >
              Revert
            </Button>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Eye className="h-3.5 w-3.5" />}
              loading={previewing}
              onClick={preview}
            >
              Preview
            </Button>
            <Button
              variant="primary"
              size="sm"
              iconLeft={<Save className="h-3.5 w-3.5" />}
              loading={saving}
              disabled={!dirty}
              onClick={save}
            >
              Save
            </Button>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1.5 block h-10 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
            />
          </label>

          <label className="block text-sm">
            <span className="font-medium text-fg-muted">HTML body</span>
            <textarea
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              rows={10}
              spellCheck={false}
              className="mt-1.5 block w-full rounded-md border border-line-strong/20 bg-surface px-3 py-2 font-mono text-xs shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
            />
          </label>

          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Plain text body</span>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              spellCheck={false}
              className="mt-1.5 block w-full rounded-md border border-line-strong/20 bg-surface px-3 py-2 font-mono text-xs shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
            />
          </label>

          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Sample variables (JSON)</span>
            <textarea
              value={sampleVars}
              onChange={(e) => setSampleVars(e.target.value)}
              rows={4}
              spellCheck={false}
              className="mt-1.5 block w-full rounded-md border border-line-strong/20 bg-surface px-3 py-2 font-mono text-xs shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
            />
            <span className="mt-1 block text-[11px] text-fg-muted">
              Used by Preview and as the default vars in the saved row.
            </span>
          </label>
        </div>
      </div>

      <div
        ref={previewRef}
        className="overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm scroll-mt-4"
      >
        <header className="flex items-center justify-between border-b border-line/10 bg-surface-2/60 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-fg-muted" />
            <span className="text-sm font-semibold">Preview</span>
            {rendered ? (
              <span className="text-[11px] text-fg-muted">
                Subject: <strong>{rendered.subject}</strong>
              </span>
            ) : null}
          </div>
          <div className="inline-flex overflow-hidden rounded-md border border-line/10">
            <button
              type="button"
              onClick={() => setPreviewMode('rendered')}
              className={`px-2.5 py-1 text-[11px] font-medium ${
                previewMode === 'rendered'
                  ? 'bg-slate-900 text-white'
                  : 'bg-surface text-fg-muted hover:bg-surface-2'
              }`}
            >
              Rendered
            </button>
            <button
              type="button"
              onClick={() => setPreviewMode('source')}
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium ${
                previewMode === 'source'
                  ? 'bg-slate-900 text-white'
                  : 'bg-surface text-fg-muted hover:bg-surface-2'
              }`}
            >
              <Code className="h-3 w-3" />
              Source
            </button>
          </div>
        </header>
        {previewError ? (
          <div className="border-b border-danger/25 bg-danger-soft/15 px-4 py-2 text-xs text-danger-soft-fg">
            {previewError}
          </div>
        ) : null}
        {!rendered ? (
          <div className="px-4 py-6 text-center text-sm text-fg-muted">
            Click <strong>Preview</strong> to render against the sample vars.
          </div>
        ) : previewMode === 'rendered' ? (
          <div className="grid gap-4 px-4 py-4 sm:grid-cols-2">
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
                HTML
              </div>
              <div
                className="prose prose-sm max-w-none rounded border border-line/10 bg-surface p-3"
                // The preview HTML is admin-authored; we trust it here.
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: rendered.html }}
              />
            </div>
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
                Plain text
              </div>
              <pre className="overflow-x-auto whitespace-pre-wrap rounded border border-line/10 bg-surface p-3 font-mono text-xs">
                {rendered.text}
              </pre>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 px-4 py-4 sm:grid-cols-2">
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
                HTML source
              </div>
              <pre className="overflow-x-auto whitespace-pre-wrap rounded border border-line/10 bg-surface-2 p-3 font-mono text-[11px]">
                {rendered.html}
              </pre>
            </div>
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
                Plain text
              </div>
              <pre className="overflow-x-auto whitespace-pre-wrap rounded border border-line/10 bg-surface-2 p-3 font-mono text-[11px]">
                {rendered.text}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
